/**
 * The household answer, in ONE shape, with ONE mapper (F35).
 *
 * "Who is moving with you?" was answered in onboarding and then asked again,
 * from scratch, by the TelePortal household step — because the answer lived in
 * four shapes typed three times, and the step read none of them:
 *
 *   • `talents.customFields.familyConfig` — the onboarding answer. Typed as
 *     `IFamilyConfig` (onboarding), `IProfileFamilyConfig` (journey-web auth
 *     types, which even dropped `hasPets`) and `HouseholdFamilyConfig`
 *     (platform-api). Three copies that had already drifted.
 *   • `journey_instances.initial_household_scope` — written by the Journey
 *     Builder with a hard-coded `childrenCount: 0`.
 *   • the capture widget's payload, and the `household_members` roster.
 *
 * This file owns the first shape and every projection OUT of it, so the three
 * places that turn a family answer into something else — the household step's
 * proposal, the Journey Builder's scope, and the roster's re-projection back
 * into `familyConfig` — cannot disagree about what "has children" means.
 *
 * Pure, dependency-free and shared by platform-api and journey-web.
 */

/** The onboarding family answer: statements about the people AROUND the applicant. */
export interface FamilyConfig {
  hasPartner: boolean;
  hasChildren: boolean;
  /** How many children. 0 when `hasChildren` is false. */
  childrenCount: number;
  hasPets: boolean;
}

/**
 * The most children a family answer may carry. The same bound the journey
 * create contract enforces on `householdConfig.childrenCount`, so a stored
 * answer this parser accepts can never be refused downstream.
 */
export const MAX_FAMILY_CHILDREN = 15;

/** Someone moving alone. */
export const SOLO_FAMILY_CONFIG: Readonly<FamilyConfig> = Object.freeze({
  hasPartner: false,
  hasChildren: false,
  childrenCount: 0,
  hasPets: false,
});

/**
 * Read a stored family answer.
 *
 * `customFields` is operator- and client-writable JSON, so this is the one
 * place the blob is trusted into a type. Returns `null` when the value is not
 * a family answer at all — "never answered" — which is a different fact from
 * `SOLO_FAMILY_CONFIG`, "answered: alone". A non-boolean flag is treated as
 * unanswered rather than coerced: `"false"` is a string, and coercing it would
 * put a partner in somebody's household.
 */
export function parseFamilyConfig(raw: unknown): FamilyConfig | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as {
    hasPartner?: unknown;
    hasChildren?: unknown;
    childrenCount?: unknown;
    hasPets?: unknown;
  };
  const flags = [r.hasPartner, r.hasChildren, r.hasPets];
  if (!flags.some((v) => typeof v === 'boolean')) return null;

  const hasChildren = r.hasChildren === true;
  const rawCount =
    typeof r.childrenCount === 'number' && Number.isFinite(r.childrenCount)
      ? Math.trunc(r.childrenCount)
      : 0;
  return {
    hasPartner: r.hasPartner === true,
    hasChildren,
    childrenCount: hasChildren ? Math.min(Math.max(rawCount, 0), MAX_FAMILY_CHILDREN) : 0,
    hasPets: r.hasPets === true,
  };
}

/** Does this answer name anybody besides the applicant? */
export function familyConfigHasMembers(config: FamilyConfig): boolean {
  return config.hasPartner || config.hasChildren || config.hasPets;
}

/**
 * Project a roster back onto the family answer (BL-146).
 *
 * The inverse of {@link proposeHouseholdFromFamilyConfig}: it takes the members
 * AROUND the applicant — the caller excludes the applicant's own row — and
 * says what the household holds.
 */
export function familyConfigFromMembers(
  familyMembers: ReadonlyArray<{ type?: string | null }>
): FamilyConfig {
  const childrenCount = familyMembers.filter((m) => m.type === 'child').length;
  return {
    hasPartner: familyMembers.some((m) => m.type === 'partner'),
    hasChildren: childrenCount > 0,
    childrenCount,
    hasPets: familyMembers.some((m) => m.type === 'pet'),
  };
}

// ═══════════════════════════════════════════════════════════════════════
// HOUSEHOLD COMPOSITION — what a company's drawer calls the household
// ═══════════════════════════════════════════════════════════════════════

/**
 * Who the applicant moves with, as one word. Pets do not change it — they
 * are not people, and `familySize` has never counted them either.
 */
export const HOUSEHOLD_COMPOSITIONS = ['solo', 'couple', 'single_parent', 'family'] as const;
export type HouseholdComposition = (typeof HOUSEHOLD_COMPOSITIONS)[number];

export interface HouseholdSummary {
  composition: HouseholdComposition;
  /** Children moving; 0 unless the composition includes children. */
  childrenCount: number;
}

/**
 * Classify a family answer. The ONE place this rule lives — it used to be a
 * ternary in the sponsored drawer, which asked "partner?" first and so called
 * a parent moving with their children, without a partner, "Solo".
 *
 * `null` for a citizen who never answered: "unknown" is not "solo". An answer
 * of children without a count counts one child, the same reading
 * {@link householdScopeFromFamilyConfig} takes.
 */
export function householdSummaryFromFamilyConfig(
  config: FamilyConfig | null
): HouseholdSummary | null {
  if (!config) return null;
  const childrenCount = config.hasChildren ? Math.max(config.childrenCount, 1) : 0;
  const composition: HouseholdComposition = config.hasPartner
    ? childrenCount > 0
      ? 'family'
      : 'couple'
    : childrenCount > 0
      ? 'single_parent'
      : 'solo';
  return { composition, childrenCount };
}

// ═══════════════════════════════════════════════════════════════════════
// JOURNEY SCOPE — `journey_instances.initial_household_scope`
// ═══════════════════════════════════════════════════════════════════════

/** The wire and storage shape of a journey's initial household claim. */
export interface InitialHouseholdScope {
  /** Everyone moving, the applicant included. At least 1. */
  familySize: number;
  hasSpouse: boolean;
  hasChildren: boolean;
  /**
   * Omitted when the answer says "children" but never said how many. The
   * previous writer filled that gap with `0`, which states "no children" in
   * the same object that says "has children".
   */
  childrenCount?: number;
}

/**
 * The scope a journey is created with, derived from the family answer.
 *
 * `familySize` counts the applicant, a partner and each child. Pets are not
 * people and are not in `familySize` — the column has never carried them.
 * When the answer has children but no count, one child is counted (the only
 * number the answer supports) and `childrenCount` is left out.
 */
export function householdScopeFromFamilyConfig(config: FamilyConfig): InitialHouseholdScope {
  const knownChildren = config.hasChildren ? config.childrenCount : 0;
  const countedChildren = config.hasChildren ? Math.max(knownChildren, 1) : 0;
  return {
    familySize: 1 + (config.hasPartner ? 1 : 0) + countedChildren,
    hasSpouse: config.hasPartner,
    hasChildren: config.hasChildren,
    ...(config.hasChildren && knownChildren === 0 ? {} : { childrenCount: knownChildren }),
  };
}

/**
 * The Journey Builder's "are you bringing family?", as the onboarding answer
 * already says it (Citizen first run slice C — the household task reads the
 * profile first). `true`/`false` when onboarding answered; `null` when it never
 * did, so the builder asks instead of presuming.
 */
export function builderFamilyAnswerFromOnboarding(onboardingFamily: unknown): boolean | null {
  const onboarding = parseFamilyConfig(onboardingFamily);
  return onboarding ? familyConfigHasMembers(onboarding) : null;
}

/**
 * The scope the Journey Builder creates a journey with.
 *
 * The builder asks one question — "are you bringing family?" — and its widget
 * used to forward the yes as `hasPartner: true`, beside a hard-coded
 * `hasChildren: false` and `childrenCount: 0`. A parent moving with three
 * children was recorded as moving with a partner and no children.
 *
 * Who is moving is the ONBOARDING answer's to say; the builder's answer only
 * says whether anybody is:
 *
 *   • "just me"                                  → a household of one;
 *   • "bringing family", onboarding names them   → that household;
 *   • "bringing family", onboarding names nobody → no claim. Left unset rather
 *     than invented; the TelePortal household step asks who.
 *   • never answered (`null`)                    → no claim either.
 */
export function initialHouseholdScopeFromBuilder(
  bringFamily: boolean | null,
  onboardingFamily: unknown
): InitialHouseholdScope | undefined {
  if (bringFamily === null) return undefined;
  if (!bringFamily) return householdScopeFromFamilyConfig(SOLO_FAMILY_CONFIG);
  const onboarding = parseFamilyConfig(onboardingFamily);
  return onboarding && familyConfigHasMembers(onboarding)
    ? householdScopeFromFamilyConfig(onboarding)
    : undefined;
}

// ═══════════════════════════════════════════════════════════════════════
// HOUSEHOLD PROPOSAL — the household step's prefill (D-17)
// ═══════════════════════════════════════════════════════════════════════

/** The member types the household step captures. The applicant is never proposed. */
export type ProposedHouseholdMemberType = 'partner' | 'child' | 'pet';

/**
 * Where a proposal came from, in the order the server consults them:
 *
 *   • `roster`     — the citizen's saved `household_members` (authoritative);
 *   • `onboarding` — no roster yet, so the onboarding `familyConfig` answer
 *                    is PROPOSED. Nothing is written until the citizen saves;
 *   • `none`       — neither exists. The step starts empty.
 */
export type HouseholdProposalSource = 'roster' | 'onboarding' | 'none';

/** One person (or pet) the household step pre-fills. */
export interface HouseholdProposalMember {
  /**
   * The member's stable roster ref. `null` for a PROPOSED member: nobody has
   * been saved yet, so there is no identity to reuse — the step mints one when
   * the citizen confirms.
   */
  clientRef: string | null;
  type: ProposedHouseholdMemberType;
  name: string;
  nationality: string | null;
  /** `null` = not answered. */
  hasPassport: boolean | null;
  /** `null` = not answered. */
  vaccinationOk: boolean | null;
  petSpecies: string | null;
}

export interface HouseholdProposal {
  source: HouseholdProposalSource;
  members: HouseholdProposalMember[];
}

/**
 * The members an onboarding answer implies: a partner, one row per child, and
 * a pet. Names and answers are empty — the onboarding step never asked them —
 * so the citizen fills them in on the step that does. An answer of "children"
 * with no count proposes one child, the same reading
 * {@link householdScopeFromFamilyConfig} takes.
 */
export function proposeHouseholdFromFamilyConfig(config: FamilyConfig): HouseholdProposalMember[] {
  const blank = (type: ProposedHouseholdMemberType): HouseholdProposalMember => ({
    clientRef: null,
    type,
    name: '',
    nationality: null,
    hasPassport: null,
    vaccinationOk: null,
    petSpecies: null,
  });
  const members: HouseholdProposalMember[] = [];
  if (config.hasPartner) members.push(blank('partner'));
  if (config.hasChildren) {
    const children = Math.max(config.childrenCount, 1);
    for (let i = 0; i < children; i++) members.push(blank('child'));
  }
  if (config.hasPets) members.push(blank('pet'));
  return members;
}
